// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import {
  ACTIVITY_FILTER_DEFAULTS,
  type ActivityEntry,
  type ActivityFilterState,
  ActivityTimeline,
} from "@/components/shared/activity-timeline";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";

function generateMockData(): ActivityEntry[] {
  const now = Date.now();
  const day = 86_400_000;
  return [
    {
      id: 1,
      timestamp: new Date(now - 0.3 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "SQL Content",
      message: "Updated JOIN clause to handle nullable store_id in source table.",
    },
    {
      id: 2,
      timestamp: new Date(now - 1 * day).toISOString(),
      user: "admin@focal.systems",
      action: "activated",
      target: "Transformation",
    },
    {
      id: 3,
      timestamp: new Date(now - 2 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Input Files",
      field: "inventory_feed.csv",
      oldValue: "optional",
      newValue: "required",
    },
    {
      id: 4,
      timestamp: new Date(now - 4 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Transformation Info",
      field: "description",
      oldValue: "Inventory daily import",
      newValue: "Inventory daily import (v2)",
    },
    {
      id: 5,
      timestamp: new Date(now - 7 * day).toISOString(),
      user: "admin@focal.systems",
      action: "deactivated",
      target: "Transformation",
      message: "Temporarily disabled while debugging duplicate row issue in staging.",
    },
    {
      id: 6,
      timestamp: new Date(now - 10 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Transformation Info",
      field: "schedule",
      oldValue: "0 */4 * * *",
      newValue: "0 * * * *",
    },
    {
      id: 7,
      timestamp: new Date(now - 15 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "created",
      target: "Input File",
      field: "promo_overrides.csv",
    },
    {
      id: 8,
      timestamp: new Date(now - 20 * day).toISOString(),
      user: "admin@focal.systems",
      action: "deleted",
      target: "Input File",
      field: "legacy_feed.tsv",
    },
    {
      id: 9,
      timestamp: new Date(now - 30 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "SQL Content",
      message: "Added WHERE clause to filter inactive stores from result set.",
    },
    {
      id: 10,
      timestamp: new Date(now - 45 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Transformation Info",
      field: "shortName",
      oldValue: "inv_import",
      newValue: "inv_daily_import",
    },
    {
      id: 11,
      timestamp: new Date(now - 60 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "activated",
      target: "Transformation",
    },
    {
      id: 12,
      timestamp: new Date(now - 75 * day).toISOString(),
      user: "ops@focal.systems",
      action: "created",
      target: "Transformation",
    },
  ];
}

const MOCK_DATA = generateMockData();

interface TransformationActivityTabProps {
  transformationId: number;
  initialFilters?: string;
}

export function TransformationActivityTab({ transformationId, initialFilters }: TransformationActivityTabProps) {
  const navigate = useNavigate();
  const initial = useMemo(() => decodeFilters(initialFilters, ACTIVITY_FILTER_DEFAULTS), [initialFilters]);

  const handleFilterChange = useCallback(
    (values: ActivityFilterState) => {
      const encoded = encodeFilters(values, ACTIVITY_FILTER_DEFAULTS);
      navigate({
        to: "/ingress/transformations/$transformationId",
        params: { transformationId: String(transformationId) },
        search: { t: "activity", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [navigate, transformationId],
  );

  return <ActivityTimeline entries={MOCK_DATA} initialFilters={initial} onFilterChange={handleFilterChange} />;
}
